/* LifestyleTracker — Sport + Psychology daily logger (iter421.4 Phase 4).
 *
 * Две групи метрики в един card с тип-toggle:
 *   • Спорт: steps, total workout min, intensity, vo2max, resting hr
 *   • Психика: sleep hours, sleep quality (1-10), mood, stress, energy
 *
 * Upsert by log_date (1 запис на ден за user). Pre-fill от today's row ако вече има.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Activity, Moon, Brain, Save, Loader2, Footprints, Heart, Smile, Frown, Sun,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const STRESS_TONE_BG = ["Спокойствие", "Лек", "Лек", "Умерен", "Умерен", "Усещам го", "Усещам го", "Доста", "Силен", "Много силен"];
const MOOD_TONE_BG   = ["Тежко", "Тежко", "Слабо", "Слабо", "OK", "OK", "Добре", "Добре", "Чудесно", "Отлично"];

function Slider1to10({ value, onChange, isDark, tone = "violet", testId }) {
  const colors = {
    violet:  "from-violet-500 to-fuchsia-500",
    emerald: "from-emerald-500 to-teal-500",
    rose:    "from-rose-500 to-pink-500",
    amber:   "from-amber-500 to-orange-500",
    sky:     "from-sky-500 to-cyan-500",
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        min="1"
        max="10"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 accent-violet-500"
        data-testid={testId}
      />
      <span className={`text-xs font-extrabold w-8 text-center rounded bg-gradient-to-r ${colors[tone]} text-white py-0.5`}>
        {value || "—"}
      </span>
    </div>
  );
}

export default function LifestyleTracker() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const today = new Date().toISOString().slice(0, 10);
  const [section, setSection] = useState("psych"); // "psych" | "sport"
  const [savingS, setSavingS] = useState(false);
  const [savingP, setSavingP] = useState(false);

  // Sport state
  const [steps, setSteps] = useState("");
  const [workoutMin, setWorkoutMin] = useState("");
  const [workoutType, setWorkoutType] = useState("run");
  const [intensity, setIntensity] = useState("moderate");
  const [vo2max, setVo2max] = useState("");
  const [restingHr, setRestingHr] = useState("");

  // Psych state
  const [sleepHours, setSleepHours] = useState("");
  const [sleepQuality, setSleepQuality] = useState(0);
  const [mood, setMood] = useState(0);
  const [stress, setStress] = useState(0);
  const [energy, setEnergy] = useState(0);

  // KPIs
  const [sportKpis, setSportKpis] = useState(null);
  const [psychKpis, setPsychKpis] = useState(null);

  const fetchTodays = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/health-intelligence/sport/recent?days=30`),
        axios.get(`${API}/health-intelligence/psych/recent?days=30`),
      ]);
      setSportKpis(s.data.kpis);
      setPsychKpis(p.data.kpis);
      const ts = (s.data.items || []).find((x) => x.log_date === today);
      const tp = (p.data.items || []).find((x) => x.log_date === today);
      if (ts) {
        setSteps(ts.steps ?? "");
        setWorkoutMin(ts.total_workout_min ?? "");
        if (Array.isArray(ts.workouts) && ts.workouts[0]) {
          setWorkoutType(ts.workouts[0].type || "run");
          setIntensity(ts.workouts[0].intensity || "moderate");
        }
        setVo2max(ts.vo2max ?? "");
        setRestingHr(ts.resting_hr ?? "");
      }
      if (tp) {
        setSleepHours(tp.sleep_hours ?? "");
        setSleepQuality(tp.sleep_quality ?? 0);
        setMood(tp.mood ?? 0);
        setStress(tp.stress ?? 0);
        setEnergy(tp.energy ?? 0);
      }
    } catch (_e) { /* mute */ }
  }, [API, today]);

  useEffect(() => { fetchTodays(); }, [fetchTodays]);

  const handleSaveSport = async () => {
    setSavingS(true);
    try {
      const payload = {
        log_date: today,
        steps: steps !== "" ? Number(steps) : undefined,
        workouts: workoutMin && Number(workoutMin) > 0
          ? [{ type: workoutType, duration_min: Number(workoutMin), intensity }]
          : [],
        vo2max: vo2max !== "" ? Number(vo2max) : undefined,
        resting_hr: restingHr !== "" ? Number(restingHr) : undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await axios.post(`${API}/health-intelligence/sport/log`, payload);
      toast.success(bg ? "Спорт лог записан ✓" : "Sport log saved ✓");
      fetchTodays();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setSavingS(false);
    }
  };

  const handleSavePsych = async () => {
    setSavingP(true);
    try {
      const payload = {
        log_date: today,
        sleep_hours: sleepHours !== "" ? Number(sleepHours) : undefined,
        sleep_quality: sleepQuality || undefined,
        mood: mood || undefined,
        stress: stress || undefined,
        energy: energy || undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await axios.post(`${API}/health-intelligence/psych/log`, payload);
      toast.success(bg ? "Психо лог записан ✓" : "Psych log saved ✓");
      fetchTodays();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setSavingP(false);
    }
  };

  const tabBtn = (id, label, Icon) => (
    <button
      key={id}
      onClick={() => setSection(id)}
      data-testid={`lifestyle-tab-${id}`}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold transition-colors ${
        section === id
          ? (isDark ? "bg-violet-500/20 text-violet-200 border-b-2 border-violet-400" : "bg-violet-50 text-violet-700 border-b-2 border-violet-500")
          : (isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"} overflow-hidden`}
          data-testid="lifestyle-tracker">
      {/* Sub-tabs */}
      <div className={`flex border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        {tabBtn("psych", bg ? "Сън · Настроение · Стрес" : "Sleep · Mood · Stress", Brain)}
        {tabBtn("sport", bg ? "Спорт · Стъпки · VO2" : "Sport · Steps · VO2", Activity)}
      </div>

      {/* PSYCH */}
      {section === "psych" && (
        <CardContent className="p-4 space-y-4" data-testid="lifestyle-psych">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Moon className="w-3 h-3" /> {bg ? "Часове сън" : "Sleep hours"}
              </label>
              <Input type="number" min="0" max="16" step="0.25" value={sleepHours}
                     onChange={(e) => setSleepHours(e.target.value)}
                     className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                     placeholder="7.5" data-testid="psych-sleep-hours" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Moon className="w-3 h-3" /> {bg ? "Качество на съня (1-10)" : "Sleep quality (1-10)"}
              </label>
              <Slider1to10 value={sleepQuality} onChange={setSleepQuality} isDark={isDark} tone="sky" testId="psych-sleep-quality" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Smile className="w-3 h-3" /> {bg ? `Настроение${mood ? ` — ${MOOD_TONE_BG[mood-1]}` : ""}` : "Mood"}
              </label>
              <Slider1to10 value={mood} onChange={setMood} isDark={isDark} tone="emerald" testId="psych-mood" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Frown className="w-3 h-3" /> {bg ? `Стрес${stress ? ` — ${STRESS_TONE_BG[stress-1]}` : ""}` : "Stress"}
              </label>
              <Slider1to10 value={stress} onChange={setStress} isDark={isDark} tone="rose" testId="psych-stress" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Sun className="w-3 h-3" /> {bg ? "Енергия" : "Energy"}
              </label>
              <Slider1to10 value={energy} onChange={setEnergy} isDark={isDark} tone="amber" testId="psych-energy" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {psychKpis && psychKpis.logged_days > 0 && (
              <p className={`text-[10.5px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? `Последни 30 дни: ${psychKpis.logged_days} дни лог-нати · ср. сън ${psychKpis.avg_sleep_hours ?? "—"}ч · ср. настроение ${psychKpis.avg_mood ?? "—"}/10` : `30d: ${psychKpis.logged_days} days logged · avg sleep ${psychKpis.avg_sleep_hours ?? "—"}h · avg mood ${psychKpis.avg_mood ?? "—"}/10`}
              </p>
            )}
            <Button onClick={handleSavePsych} disabled={savingP}
                    className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white ml-auto"
                    data-testid="psych-save">
              {savingP ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {bg ? "Запиши днешния ден" : "Save today"}
            </Button>
          </div>
        </CardContent>
      )}

      {/* SPORT */}
      {section === "sport" && (
        <CardContent className="p-4 space-y-4" data-testid="lifestyle-sport">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Footprints className="w-3 h-3" /> {bg ? "Стъпки" : "Steps"}
              </label>
              <Input type="number" min="0" max="100000" step="100" value={steps}
                     onChange={(e) => setSteps(e.target.value)}
                     className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                     placeholder="8500" data-testid="sport-steps" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Activity className="w-3 h-3" /> {bg ? "Тренировка (мин)" : "Workout (min)"}
              </label>
              <Input type="number" min="0" max="600" step="5" value={workoutMin}
                     onChange={(e) => setWorkoutMin(e.target.value)}
                     className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                     placeholder="45" data-testid="sport-workout-min" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold mb-1 block ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Тип" : "Type"}
              </label>
              <select value={workoutType} onChange={(e) => setWorkoutType(e.target.value)}
                      className={`w-full h-9 rounded-md px-2 text-sm ${isDark ? "bg-slate-900 text-white border border-slate-700" : "bg-white text-slate-900 border border-slate-300"}`}
                      data-testid="sport-type">
                <option value="walk">{bg ? "Ходене" : "Walk"}</option>
                <option value="run">{bg ? "Бягане" : "Run"}</option>
                <option value="cycling">{bg ? "Колоездене" : "Cycling"}</option>
                <option value="weights">{bg ? "Тежести" : "Weights"}</option>
                <option value="hiit">HIIT</option>
                <option value="swim">{bg ? "Плуване" : "Swim"}</option>
                <option value="yoga">{bg ? "Йога / Пилатес" : "Yoga / Pilates"}</option>
                <option value="other">{bg ? "Друго" : "Other"}</option>
              </select>
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold mb-1 block ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Интензивност" : "Intensity"}
              </label>
              <select value={intensity} onChange={(e) => setIntensity(e.target.value)}
                      className={`w-full h-9 rounded-md px-2 text-sm ${isDark ? "bg-slate-900 text-white border border-slate-700" : "bg-white text-slate-900 border border-slate-300"}`}
                      data-testid="sport-intensity">
                <option value="low">{bg ? "Лека" : "Low"}</option>
                <option value="moderate">{bg ? "Умерена" : "Moderate"}</option>
                <option value="high">{bg ? "Висока" : "High"}</option>
                <option value="vigorous">{bg ? "Изтощителна" : "Vigorous"}</option>
              </select>
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Heart className="w-3 h-3" /> VO2max (ml/kg/min)
              </label>
              <Input type="number" min="15" max="85" step="0.5" value={vo2max}
                     onChange={(e) => setVo2max(e.target.value)}
                     className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                     placeholder="42" data-testid="sport-vo2max" />
            </div>
            <div>
              <label className={`text-[10.5px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                <Heart className="w-3 h-3" /> {bg ? "Пулс в покой" : "Resting HR"}
              </label>
              <Input type="number" min="30" max="150" step="1" value={restingHr}
                     onChange={(e) => setRestingHr(e.target.value)}
                     className={`${isDark ? "bg-slate-900 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`}
                     placeholder="60" data-testid="sport-resting-hr" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            {sportKpis && sportKpis.avg_steps != null && (
              <p className={`text-[10.5px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? `30д: ${sportKpis.avg_steps} ср. стъпки · ${sportKpis.total_workout_min} мин тренировка · ${sportKpis.active_days} активни дни` : `30d: ${sportKpis.avg_steps} avg steps · ${sportKpis.total_workout_min}min workout · ${sportKpis.active_days} active days`}
              </p>
            )}
            <Button onClick={handleSaveSport} disabled={savingS}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white ml-auto"
                    data-testid="sport-save">
              {savingS ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {bg ? "Запиши днешния ден" : "Save today"}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
